import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import CitySearchField from "../components/CitySearchField";
import VenueSearchField from "../components/VenueSearchField";
import BookingCart from "../components/BookingCart";
import AddArtistToCartModal from "../components/AddArtistToCartModal";
import PaymentStep from "../components/booking/PaymentStep";
import ReviewStep from "../components/booking/ReviewStep";
import TechRiderLink from "../components/TechRiderLink";
import { buildOutstationNotice } from "../components/booking/ReviewStep";
import api, { fmtINRFull, formatApiError, mediaUrl } from "../lib/api";
import { loadIndianCityCatalog } from "../lib/indianCities";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";
import { useEventCart } from "../lib/useEventCart";
import { hoursUntilEvent } from "../lib/bookingPaymentTiming";
import { getArtistBookingMusicPrompt } from "../lib/artistBookingQuestions";
import { markBookingHandoff, setTrackingPage, track } from "../analytics/tracker";
import useBookingStepTracking from "../analytics/useBookingStepTracking";
import { addDaysToISODate, formatPaymentDueLabel, formatTimelineDate, resolvePaymentSchedule, todayISODate } from "../lib/paymentTimeline";
import PaymentTimeline from "../components/booking/PaymentTimeline";
import {
  eventBlockInterval,
  eventPreparationMinutes,
  intervalsConflict,
  isTodayEventDate,
  isPastEventStart,
  parseDurationMinutes,
  parseTimeMinutes,
} from "../lib/bookingAvailability";

const ADDONS = [
  { id: "dhol", label: " Dhol Player", price: 3500 },
  { id: "anchor", label: " Anchor / Emcee", price: 5000 },
  { id: "photo", label: " Photography", price: 4000 },
  { id: "extra-hour", label: " Extra Hour", price: 8000 },
];

// Iter 52.5 — Time slots now carry a mood-label so the grid reads like a
// premium concierge picker ("6:00 PM · Evening") instead of a bare list.
// Artist availability window: 8:00 AM to 2:00 AM.
const TIME_SLOTS = [
  { time: "8:00 AM", label: "Morning" },
  { time: "9:00 AM", label: "Morning" },
  { time: "10:00 AM", label: "Morning" },
  { time: "11:00 AM", label: "Late Morning" },
  { time: "12:00 PM", label: "Noon" },
  { time: "1:00 PM", label: "Afternoon" },
  { time: "2:00 PM", label: "Matinee" },
  { time: "3:00 PM", label: "Afternoon" },
  { time: "4:00 PM", label: "Afternoon" },
  { time: "5:00 PM", label: "Evening" },
  { time: "6:00 PM", label: "Evening" },
  { time: "7:00 PM", label: "Evening" },
  { time: "8:00 PM", label: "Prime" },
  { time: "9:00 PM", label: "Popular" },
  { time: "10:00 PM", label: "Late" },
  { time: "11:00 PM", label: "Late" },
  { time: "12:00 AM", label: "Midnight" },
  { time: "1:00 AM", label: "Late Night" },
  // { time: "2:00 AM", label: "Night" },
];

export default function BookingFlow() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const toast = useToast();
  const nav = useNavigate();

  const [artist, setArtist] = useState(null);
  const [artistPreviewOpen, setArtistPreviewOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [availabilitySchedule, setAvailabilitySchedule] = useState(null);
  const [availabilityScheduleCity, setAvailabilityScheduleCity] = useState("");
  const [availabilityLoadFailed, setAvailabilityLoadFailed] = useState(false);
  const [cityRequiredNotice, setCityRequiredNotice] = useState(false);
  const [artistAddons, setArtistAddons] = useState([]); // Sprint 3
  const [cityOptions, setCityOptions] = useState([]);
  const [popularCityOptions, setPopularCityOptions] = useState([]);
  const [cityAliases, setCityAliases] = useState({});
  const [cityOptionsError, setCityOptionsError] = useState(false);
  const [platformSettings, setPlatformSettings] = useState({});  // Outstation policy strings
  const [paymentSettings, setPaymentSettings] = useState({});
  const [busy, setBusy] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  // Iter 73 — Persist multi-step booking state to sessionStorage so:
  //   (a) hitting Back inside the flow never loses previously entered data,
  //   (b) an auth bounce (login/signup) followed by a return-to-URL lands
  //       the customer on the SAME step with the SAME selections,
  //   (c) an accidental refresh doesn't reset the funnel to step 1.
  // Key is scoped to the artist so switching artists doesn't cross-pollute.
  const storageKey = `bt_book_state_${id}`;
  const restored = React.useMemo(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, [storageKey]);
  const [step, setStep] = useState(restored?.step || 1);

  const [form, setForm] = useState(() => {
    const base = {
      package_id: params.get("pkg") || "",
      addons: [],
      addon_selections: [],  // Sprint 3: [{addon_id, quantity}]
      // Iter 44 — pre-fill from URL for "Add another artist to this event" flow
      event_date: params.get("date") || "",
      event_time: params.get("time") || "",
      event_type: params.get("event_type") || "",
      event_type_selected: Boolean(params.get("event_type")),
      venue: params.get("venue") || "",
      city: params.get("city") || "",
      guests: "300-600",
      language_pref: "Hindi (Bollywood)",
      notes: "",
      customer_name: user ? `${user.first_name} ${user.last_name || ""}`.trim() : "",
      customer_phone: user?.phone || "",
      customer_email: user?.email || "",
      coupon_code: "",
      // Iter 52.5 additions ↓
      customer_travel_allowance: "",
      tnc_accepted: false,
      // Iter 52.6 — outstation ack pre-collected on the artist profile
      outstation_ack: params.get("outstation_ack") === "1",
      travel_terms_acknowledged: params.get("travel_terms_acknowledged") === "1" || params.get("outstation_ack") === "1",
    };
    // Merge in any persisted form values — URL params still override so a
    // "share this booking" link keeps the same explicit values.
    if (restored?.form) {
      Object.keys(restored.form).forEach((k) => {
        if (
          k === "event_type"
          && !params.get("event_type")
          && !restored.form.event_type_selected
          && restored.form.event_type === "Wedding / Sangeet"
        ) return;
        if (!params.get(k === "package_id" ? "pkg" : k) && restored.form[k] !== undefined) {
          base[k] = restored.form[k];
        }
      });
    }
    return base;
  });
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [successData, setSuccessData] = useState(null);
  const [gatewayInfo, setGatewayInfo] = useState({ provider: "easebuzz", enabled: true, environment: "sandbox" });
  const [quoteMeta, setQuoteMeta] = useState(null); // full breakdown from /finance/quote
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [alternatives, setAlternatives] = useState(null);
  const bookingStartedTracked = React.useRef(false);
  // Iter 44 — Multi-Artist Event: if we came in from another booking's
  // "Add another artist" strip, pre-fill event basics and thread the
  // event_id through so the new booking joins the same umbrella.
  const eventIdParam = params.get("event_id") || "";
  const [suggested, setSuggested] = useState([]);  // shown on success screen
  const bookingStartedAt = React.useRef(Date.now()).current;
  useBookingStepTracking(step, id, bookingStartedAt);

  // Analytics: attach this journey's context to every booking/payment event,
  // and stop exit tracking once the booking is confirmed.
  useEffect(() => {
    setTrackingPage({ journey: {
      artist_id: id,
      category: artist?.profile?.category,
      city: form.city || undefined,
      event_type: form.event_type || undefined,
      event_date: form.event_date || undefined,
    } });
  }, [id, artist, form.city, form.event_type, form.event_date]);
  useEffect(() => {
    if (step === 6) markBookingHandoff();
  }, [step]);

  useEffect(() => {
    if (!artist || bookingStartedTracked.current) return;
    bookingStartedTracked.current = true;
    track("booking_started", {
      artist_id: id,
      event_type: form.event_type || undefined,
      event_date: form.event_date || undefined,
      city: form.city || undefined,
      amount_band: quoteMeta?.total ? Math.floor(Number(quoteMeta.total) / 25000) * 25000 : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artist, id, form.event_type, form.event_date, form.city, quoteMeta]);

  useEffect(() => {
    const interval = window.setInterval(() => setCurrentTime(new Date()), 15000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!artistPreviewOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setArtistPreviewOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [artistPreviewOpen]);

  useEffect(() => {
    if (!user) {
      // Iter 73 — Preserve the full booking URL (including pkg/city/date/
      // outstation_ack) so login → sign-up flow returns the customer to
      // this exact step with everything they had already selected.
      const back = `${window.location.pathname}${window.location.search}`;
      nav(`/login?next=${encodeURIComponent(back)}`);
      return;
    }
    api.get(`/artists/${id}`).then((r) => {
      setArtist(r.data);
      const artistPackages = Array.isArray(r.data.packages) ? r.data.packages : [];
      setPackages(artistPackages);
      setForm((current) => {
        if (artistPackages.some((p) => p.id === current.package_id)) return current;
        const fallback = artistPackages.find((p) => p.is_popular) || artistPackages[0];
        return { ...current, package_id: fallback?.id || "" };
      });
    });
    // Iter 74 — Cross-device draft restore. Only apply the server draft
    // when we DON'T have a local sessionStorage state (that always wins
    // because it's more recent) AND no URL params provided pkg/city/date
    // (a shared deep-link must also win). This makes "start on desktop,
    // finish on phone" work seamlessly.
    if (!restored && !params.get("pkg") && !params.get("city") && !params.get("date")) {
      api.get(`/customer/booking-drafts/${id}`).then((r) => {
        if (r.data?.form) {
          const draftForm = { ...r.data.form };
          if (draftForm.event_type === "Wedding / Sangeet" && !draftForm.event_type_selected) {
            draftForm.event_type = "";
          }
          setForm((f) => ({ ...f, ...draftForm }));
          if (typeof r.data.step === "number" && r.data.step > 1 && r.data.step < 6) {
            setStep(r.data.step);
          }
        }
      }).catch(() => { /* no draft — normal path */ });
    }
    // Sprint 3 — load artist-defined add-ons
    api.get(`/artists/${id}/addons`).then((r) => {
      setArtistAddons(r.data || []);
      // Auto-select mandatory add-ons
      const mandatory = (r.data || []).filter((a) => a.is_mandatory).map((a) => ({ addon_id: a.id, quantity: 1 }));
      if (mandatory.length) setForm((f) => ({ ...f, addon_selections: mandatory }));
    }).catch(() => setArtistAddons([]));
    // Outstation policy strings — admin-editable via /admin/settings
    api.get("/settings/public").then((r) => setPlatformSettings(r.data || {})).catch(() => {});
    loadIndianCityCatalog("event")
      .then(({ popular, cities, aliases }) => {
        setPopularCityOptions(popular.map((city) => city.name));
        setCityOptions(cities.map((city) => city.name));
        setCityAliases(aliases);
      })
      .catch(() => setCityOptionsError(true));
    // The payment summary must use the same schedule that checkout enforces.
    api.get("/platform-settings/public").then((r) => setPaymentSettings(r.data || {})).catch(() => {});
    // Iter 61 — Admin-configurable active payment gateway (Easebuzz only).
    api.get("/payment-gateway/public").then((r) => setGatewayInfo(r.data)).catch(() => {});
    // Fetch only when the artist/package `id` changes. The canonical quote
    // effect below waits for the package and add-ons before requesting a
    // price, preventing a package_fee=0 response from overwriting it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Iter 44 — Load complementary artists once the booking is confirmed so we
  // can show the "Complete your event" strip on the success screen.
  useEffect(() => {
    if (step !== 6 || !successData?.booking?.artist_id) return;
    const params = new URLSearchParams();
    if (successData.booking.event_date) params.set("date_str", successData.booking.event_date);
    params.set("limit", "6");
    api
      .get(`/artists/${successData.booking.artist_id}/suggested?${params.toString()}`)
      .then((r) => setSuggested(Array.isArray(r.data) ? r.data : (r.data?.suggested || r.data?.artists || [])))
      .catch(() => setSuggested([]));
  }, [step, successData]);

  const set = (k, v) => setForm((current) => ({
    ...current,
    [k]: v,
    ...(k === "city" && v !== current.city && current.city.trim() ? { event_time: "" } : {}),
    ...(k === "city" && v !== current.city
      ? { travel_terms_acknowledged: false, travel_ack: false, outstation_ack: false }
      : {}),
  }));
  const toggleAddon = (a) => set("addons", form.addons.includes(a) ? form.addons.filter(x => x !== a) : [...form.addons, a]);

  // Iter 73 — Auto-persist form + step to sessionStorage on every change
  // so refresh / back / login-bounce can restore the exact state. We
  // clear on step 6 (success) so the next booking starts fresh.
  React.useEffect(() => {
    try {
      if (step === 6) {
        sessionStorage.removeItem(storageKey);
      } else {
        sessionStorage.setItem(storageKey, JSON.stringify({ form, step }));
      }
    } catch { /* private-mode / quota — silently ignore */ }
  }, [form, step, storageKey]);

  // Iter 74 — Cross-device draft sync. Debounce writes 1.2s so we don't
  // spam the backend on every keystroke, but still preserve on nav-away
  // or unmount. Cleared server-side once the booking hits step 6.
  const draftSyncTimer = React.useRef(null);
  React.useEffect(() => {
    if (!user || step === 6 || !id) return;
    // Only push meaningful drafts — skip the mount-time no-op call.
    if (step === 1 && !form.package_id && !form.city && !form.event_date) return;
    clearTimeout(draftSyncTimer.current);
    draftSyncTimer.current = setTimeout(() => {
      api.post("/customer/booking-drafts", { artist_id: id, form, step })
        .catch(() => {}); // silent — sessionStorage still holds the state
    }, 1200);
    return () => clearTimeout(draftSyncTimer.current);
  }, [form, step, id, user]);

  React.useEffect(() => {
    if (step === 6 && id) {
      api.delete(`/customer/booking-drafts/${id}`).catch(() => {});
    }
  }, [step, id]);

  // Iter 74 — Browser-Back walks the wizard. Every step change (except
  // the initial mount) pushes a new history entry keyed with `{step}`.
  // A popstate handler intercepts Back to decrement the step in-place
  // instead of letting the browser tear the whole booking flow down.
  // Step 6 (success) is a terminal state → we do NOT push it so Back
  // takes the user out of the wizard entirely (expected UX).
  const initialStepRef = React.useRef(step);
  React.useEffect(() => {
    if (step === initialStepRef.current) return; // don't push the mount step
    if (step === 6) return; // terminal → let Back leave the flow
    try {
      window.history.pushState({ __bt_step: step, __bt_artist: id }, "", window.location.href);
    } catch { /* ignore */ }
  }, [step, id]);
  React.useEffect(() => {
    const onPop = (e) => {
      // Ignore popstate for other pages / artists.
      const s = e.state?.__bt_step;
      if (typeof s === "number" && e.state?.__bt_artist === id) {
        setStep(s);
        return;
      }
      // No step in state → user is exiting the flow entirely; if we're
      // past step 1 we treat this as walking back one step so single Back
      // presses inside the wizard don't blow it up. Only decrement if
      // there IS a previous step to walk to.
      if (step > 1 && step < 6) {
        setStep((s) => Math.max(1, s - 1));
        try {
          window.history.pushState({ __bt_step: step - 1, __bt_artist: id }, "", window.location.href);
        } catch { /* ignore */ }
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, id]);

  // Iter 35 — Canonicalise cities before comparing so intra-region events
  // (Delhi/New Delhi/NCR, Mumbai/Bombay, Bengaluru/Bangalore, ...) don't
  // wrongly trigger the outstation gate. Uses the same alias table as the
  // backend, exposed via /settings/public.
  const canonicalCity = (name) => {
    const key = (name || "").trim().toLowerCase().replace(/\s+/g, " ");
    if (!key) return "";
    const table = platformSettings.city_aliases || {};
    for (const [canon, aliases] of Object.entries(table)) {
      if (canon.toLowerCase() === key) return canon.toLowerCase();
      if (Array.isArray(aliases) && aliases.some((a) => (a || "").toLowerCase() === key)) return canon.toLowerCase();
    }
    return key;
  };
  const isOutstation = !!(
    form.city &&
    artist?.profile?.city &&
    canonicalCity(form.city) !== canonicalCity(artist.profile.city)
  );

  // Sprint 3 — Artist-defined add-on helpers
  const isAddonSelected = (id) => form.addon_selections.some((x) => x.addon_id === id);
  const addonQty = (id) => form.addon_selections.find((x) => x.addon_id === id)?.quantity || 0;
  const toggleArtistAddon = (a) => {
    if (a.is_mandatory) return; // cannot deselect mandatory
    setForm((f) => ({
      ...f,
      addon_selections: isAddonSelected(a.id)
        ? f.addon_selections.filter((x) => x.addon_id !== a.id)
        : [...f.addon_selections, { addon_id: a.id, quantity: 1 }],
    }));
  };
  const setArtistAddonQty = (a, qty) => {
    const requestedQty = Math.floor(Number(qty) || 0);
    setForm((f) => {
      if (requestedQty < 1 && !a.is_mandatory) {
        return {
          ...f,
          addon_selections: f.addon_selections.filter((x) => x.addon_id !== a.id),
        };
      }
      const q = Math.max(1, Math.min(a.max_quantity || 1, requestedQty || 1));
      return {
        ...f,
        addon_selections: f.addon_selections.map((x) =>
          x.addon_id === a.id ? { ...x, quantity: q } : x,
        ),
      };
    });
  };

  const pkg = packages.find((p) => p.id === form.package_id);
  const bookingDurationMinutes = parseDurationMinutes(pkg?.duration);
  const bookingPrepBufferMinutes = eventPreparationMinutes(artist?.profile?.answers);
  const scheduleForDate = {
    blocked: (availabilitySchedule?.blocked_slots || []).filter((slot) => slot.date === form.event_date),
    booked: (availabilitySchedule?.booked_slots || []).filter((slot) => slot.date === form.event_date),
  };
  const isTimeSlotAvailable = (time) => {
    const start = parseTimeMinutes(time);
    if (
      start == null || !form.event_date
      || isTodayEventDate(form.event_date, currentTime)
      || isPastEventStart(form.event_date, time, currentTime)
    ) return false;
    if (!form.city.trim()) return true;
    if (availabilityScheduleCity !== form.city || !availabilitySchedule) return false;
    const [blockedStart, end] = eventBlockInterval(
      start,
      bookingDurationMinutes,
      bookingPrepBufferMinutes,
    );
    if ((availabilitySchedule?.blocked_dates || []).includes(form.event_date)) return false;
    if (scheduleForDate.blocked.some((slot) =>
      intervalsConflict(blockedStart, end, slot.start_minute, slot.end_minute)
    )) return false;
    return !scheduleForDate.booked.some((slot) =>
      intervalsConflict(blockedStart, end, slot.start_minute, slot.end_minute, slot.travel_buffer_minutes || 0)
    );
  };
  const selectedTimeAvailable = isTimeSlotAvailable(form.event_time);
  const continueToEventInfo = () => {
    if (form.city.trim().length < 2) {
      track("booking_validation_error", { step: 2, field_names: ["city"] });
      setCityRequiredNotice(true);
      const cityInput = document.getElementById("booking-event-city");
      cityInput?.scrollIntoView({ behavior: "smooth", block: "center" });
      cityInput?.focus({ preventScroll: true });
      return;
    }
    if (!form.event_date || !form.event_time || !selectedTimeAvailable) {
      track("booking_validation_error", {
        step: 2,
        field_names: [
          ...(!form.event_date ? ["event_date"] : []),
          ...(!form.event_time ? ["event_time"] : []),
          ...(form.event_time && !selectedTimeAvailable ? ["event_time_unavailable"] : []),
        ],
      });
      return;
    }
    setCityRequiredNotice(false);
    setStep(3);
  };
  const continueToReview = () => {
    const requiredFields = [
      ...(!form.customer_name.trim() ? ["customer_name"] : []),
      ...(!form.customer_phone.trim() ? ["customer_phone"] : []),
      ...(!form.customer_email.trim() ? ["customer_email"] : []),
      ...(!form.event_type ? ["event_type"] : []),
      ...(!form.venue.trim() ? ["venue"] : []),
      ...(!form.venue_address?.trim() ? ["venue_address"] : []),
      ...(form.event_type === "Others" && !form.event_type_other?.trim() ? ["event_type_other"] : []),
    ];
    if (requiredFields.length) {
      track("booking_validation_error", { step: 3, field_names: requiredFields });
      return;
    }
    setStep(4);
  };
  const hasEventCity = form.city.trim().length >= 2;
  const scheduleContinueDisabled = !form.event_date
    || (hasEventCity && (!form.event_time || !selectedTimeAvailable));
  const musicRequestPrompt = getArtistBookingMusicPrompt(artist?.profile);
  const addonsTotal = form.addons.reduce((s, a) => s + (ADDONS.find(x => x.id === a)?.price || 0), 0);
  // Add-on prices are final; booking add-ons do not have a GST surcharge.
  const artistAddonsTotal = form.addon_selections.reduce((s, sel) => {
    const a = artistAddons.find((x) => x.id === sel.addon_id);
    if (!a) return s;
    return s + a.price * sel.quantity;
  }, 0);
  const pkgPrice = pkg?.price || 0;
  const primarySubtotal = pkgPrice + addonsTotal + artistAddonsTotal;

  // ── Iter 45/46 — Multi-Artist Event Cart via useEventCart hook ──────────
  // Secondary artists added via the "Need More Artists?" panel on Step 2.
  // The primary artist is derived from the current form state and lives at
  // cartItems[0]. Secondary artists persist to localStorage keyed by primary
  // artist id — so a coffee-break refresh brings them back.
  const {
    extraArtists,
    addModalArtist,
    cartItems,
    cartArtistIds,
    cartPricing,
    isMultiEvent,
    setAddModalArtist,
    addSecondaryArtist,
    removeSecondaryArtist,
    clearCart,
  } = useEventCart({
    id,
    artist,
    pkg,
    form,
    primarySubtotal,
    legacyAddonsMeta: ADDONS,
    toast,
  });
  const eventTravelItems = cartItems.map((item) => ({
    ...item,
    is_outstation: Boolean(
      form.city && item.city && canonicalCity(form.city) !== canonicalCity(item.city)
    ),
  }));

  // ── BookTalent business model ─────────────────────────────────────
  // Backend is the ONLY source of truth for pricing. We call /finance/quote
  // whenever the base inputs change and read platform_fee / gst / total
  // from the response — no more hardcoded 5% / 18% on the frontend.
  const artistFee = primarySubtotal;                    // paid directly to artist
  const platformFee = quoteMeta ? Math.round(quoteMeta.platform_fee || 0)         : Math.round(artistFee * 0.05);
  const platformFeeNet = quoteMeta ? Math.round(quoteMeta.platform_fee_net || 0) : platformFee;
  const gst = 0;
  const gstPercent = 0;
  const feePercent = quoteMeta?.platform_fee_percent ?? 5;
  const gstVisible = quoteMeta ? !!quoteMeta.gst_visible : true;
  // The first checkout collects the currently due schedule installment.
  const total = quoteMeta?.total ?? (platformFeeNet + gst);
  // The schedule checkout will create, resolved exactly like the backend
  // (financial_engine.resolve_payment_schedule) from admin settings:
  // instalments already due move into the advance, and short-notice
  // bookings collect at least the admin's minimum upfront. A missing event
  // time counts as 12:00 AM, as on the server.
  const hoursToEvent = hoursUntilEvent(form.event_date, form.event_time || "12:00 AM");
  const paymentSchedule = resolvePaymentSchedule(
    paymentSettings.payment_schedule || [],
    form.event_date,
    todayISODate(),
    hoursToEvent,
    paymentSettings.instant_book_rules || {},
  );
  const bookingAdvance = paymentSchedule.find((row) => row.offset_days == null) || paymentSchedule[0];
  const paymentDuePercent = Number(bookingAdvance?.percent ?? 30);
  const bookingTotal = isMultiEvent
    ? Number(cartPricing.payment_schedule_total || 0)
    : Number(quoteMeta?.payment_schedule_total ?? total);
  const paymentDue = Math.round(bookingTotal * paymentDuePercent) / 100;
  const remainingAfterAdvance = Math.max(0, bookingTotal - paymentDue);
  // Each row: name, exact calendar date, timing relative to the event, amount.
  // Like the backend, the last instalment absorbs rounding so rows sum to the total.
  const paymentTimeline = (() => {
    if (!bookingTotal || !paymentSchedule.length) return [];
    let remaining = bookingTotal;
    return paymentSchedule.map((milestone, index) => {
      const isLast = index === paymentSchedule.length - 1;
      const amount = isLast ? Math.round(remaining * 100) / 100 : Math.round(bookingTotal * Number(milestone.percent || 0)) / 100;
      remaining -= amount;
      if (milestone.offset_days == null) {
        const dueDate = todayISODate();
        return {
          label: "Booking advance",
          amount,
          due_date: dueDate,
          dateText: formatTimelineDate(dueDate),
          relative: `At booking confirmation · ${Number(milestone.percent)}%`,
          dueLabel: "At booking confirmation",
        };
      }
      const dueDate = addDaysToISODate(form.event_date, milestone.offset_days);
      return {
        label: `Payment ${index + 1}`,
        amount,
        due_date: dueDate,
        dateText: formatTimelineDate(dueDate),
        relative: `${formatPaymentDueLabel(milestone)} · ${Number(milestone.percent)}%`,
        dueLabel: formatPaymentDueLabel(milestone),
      };
    });
  })();

  // Re-fetch canonical quote when pricing inputs change so the summary
  // stays perfectly in sync with what the server will actually charge.
  useEffect(() => {
    if (!id) return;
    const packageFee = pkgPrice + addonsTotal + artistAddonsTotal;
    if (packageFee <= 0) return;
    setQuoteLoading(true);
    api.get(`/finance/quote?artist_id=${id}&package_fee=${packageFee}`)
      .then((r) => setQuoteMeta(r.data))
      .catch(() => { /* keep last good quote */ })
      .finally(() => setQuoteLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pkgPrice, addonsTotal, artistAddonsTotal]);

  const submitBooking = async () => {
    setBusy(true);
    setAlternatives(null);

    // Iter 59.1 — DEFENSIVE session + T&C check.
    // User reported "randomly redirects to login / T&C error / Not
    // Authorized" after clicking Pay. Root cause was any one of:
    //   (a) form state stale/unset for hidden fields → 422
    //   (b) `tnc_accepted` un-ticked between step 4 and Pay → 400
    //   (c) transient 401 on a background call
    // We now:
    //   1) Probe /auth/me — if it 401s we still let the flow continue so
    //      /bookings can produce the authoritative error (the api.js
    //      interceptor rewrites 401s to a friendly message). Previously we
    //      short-circuited here with a "session ended" toast that fired
    //      even when the real cookie was perfectly valid.
    //   2) Explicitly build the payload — NEVER spread `...form` — so
    //      only the fields the backend expects are sent, with correct
    //      types. tnc_accepted is HARD-FORCED to true (user is on step 5
    //      which means they cleared the ReviewStep T&C gate on step 4).
    try {
      await api.get("/auth/me");
    } catch (_e) {
      // Non-fatal — /bookings will re-check auth and produce a real error.
      // eslint-disable-next-line no-console
      console.warn("[booking] /auth/me probe failed, continuing anyway");
    }

    // Iter 62 — Defensive mandatory-addon merge. The backend rejects a
    // booking if any artist-defined `is_mandatory` add-on is missing. We
    // auto-select these on mount, but a race condition or user's own
    // deselect action could still leave them out at submit time — so re-
    // merge them here as a belt-and-braces guarantee.
    const mandatoryAddons = (artistAddons || []).filter((a) => a.is_mandatory && a.active !== false);
    const currentSels = form.addon_selections || [];
    const mergedSelections = [...currentSels];
    for (const md of mandatoryAddons) {
      if (!mergedSelections.some((x) => x.addon_id === md.id)) {
        mergedSelections.push({ addon_id: md.id, quantity: 1 });
      }
    }

    const commonFields = {
      event_date: form.event_date,
      event_time: form.event_time,
      event_type: form.event_type,
      // Iter 83 — Sec 20/21/22 new fields
      event_type_other: form.event_type === "Others" ? (form.event_type_other || "") : null,
      number_of_days: parseInt(form.number_of_days) || 1,
      venue_address: form.venue_address || "",
      venue: form.venue,
      city: form.city.trim(),
      guests: (form.guests || form.guest_count || "").toString(),
      language_pref: form.language_pref || "",
      notes: musicRequestPrompt ? (form.notes || "").trim() : "",
      special_instructions: form.special_instructions || "",
      customer_name: form.customer_name || user?.first_name || "",
      customer_phone: form.customer_phone || "",
      customer_email: form.customer_email || user?.email || "",
      tnc_accepted: true, // forced — user is past step 4 gate
      travel_terms_acknowledged: Boolean(form.travel_terms_acknowledged || form.travel_ack || form.outstation_ack),
      customer_travel_allowance:
        form.customer_travel_allowance === "" || form.customer_travel_allowance == null
          ? 0
          : Number(form.customer_travel_allowance) || 0,
    };

    try {
      // ── Iter 45: Multi-Artist Batch Checkout (Easebuzz-only) ─────────
      // All bookings created under one event_id and paid in a single hosted
      // Easebuzz checkout. Each artist still gets an isolated booking,
      // contract and 24-hour confirmation window.
      if (isMultiEvent) {
        const items = cartItems.map((c) => ({
          artist_id: c.artist_id,
          package_id: c.package_id,
          addons: c.is_primary ? form.addons : [],
          addon_selections: c.is_primary ? mergedSelections : (c.addon_selections || []),
          coupon_code: c.is_primary ? form.coupon_code : "",
          ...commonFields,
        }));
        const batchR = await api.post("/bookings/batch", { items });
        const { booking_ids } = batchR.data;
        const ebR = await api.post("/payments/easebuzz/init", {
          booking_ids, method: paymentMethod,
        });
        track("payment_initiated", { booking_id: booking_ids[0], amount_band: Math.floor(Number(paymentDue) / 25000) * 25000 });
        markBookingHandoff();
        window.location.href = ebR.data.payment_url;
        return;
      }
      // ── Single-artist flow — explicit payload ─────────────────────────
      const payload = {
        artist_id: id,
        package_id: form.package_id,
        addons: form.addons || [],
        addon_selections: mergedSelections,
        coupon_code: form.coupon_code || "",
        ...commonFields,
        ...(eventIdParam ? { event_id: eventIdParam } : {}),
      };
      let r;
      try {
        r = await api.post("/bookings", payload);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (typeof detail === "object" && !Array.isArray(detail) && detail?.alternatives) {
          setAlternatives({ message: detail.message, date: detail.date, list: detail.alternatives });
          setBusy(false);
          return;
        }
        throw e;
      }
      const booking = r.data;
      const ebR = await api.post("/payments/easebuzz/init", {
        booking_ids: [booking.id], method: paymentMethod,
      });
      track("payment_initiated", { booking_id: booking.id, amount_band: Math.floor(Number(paymentDue) / 25000) * 25000 });
      markBookingHandoff();
      window.location.href = ebR.data.payment_url;
      return;
    } catch (e) {
      track("payment_failed", { reason_code: e?.response?.status ? `http_${e.response.status}` : "initiation_error" });
      toast(formatApiError(e), "error");
    }
    setBusy(false);
  };

  if (!artist) return (
    <div><Nav /><div className="loading"><div className="spinner" /></div></div>
  );

  const openArtistPreview = () => setArtistPreviewOpen(true);
  const artistEventTypes = artist.profile.event_types || artist.profile.answers?.event_types || [];
  const artistEventTypeList = Array.isArray(artistEventTypes)
    ? artistEventTypes
    : String(artistEventTypes).split(/[,|]/).map((value) => value.trim()).filter(Boolean);
  const artistBio = (artist.profile.bio || artist.profile.answers?.about || "").trim();
  const artistBioWords = artistBio.split(/\s+/).filter(Boolean);
  const artistBioPreview = artistBioWords.length > 36
    ? `${artistBioWords.slice(0, 36).join(" ")}…`
    : artistBio;

  return (
    <div data-testid="booking-flow-page">
      <Nav />
      <div className="container" style={{ paddingTop: "var(--space-8)", paddingBottom: "var(--space-16)" }}>
        {step < 6 && (
          <div className="booking-profile-link-row">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={openArtistPreview}
              data-testid="booking-view-artist-profile"
            >View {artist.profile.stage_name}&apos;s details</button>
          </div>
        )}
        {step < 6 && (
          <div className="steps" data-testid="booking-steps">
            {[1, 2, 3, 4, 5].map((n, i) => (
              <React.Fragment key={n}>
                <div className="step-node">
                  <div className={`step-circle ${step === n ? "active" : step > n ? "done" : ""}`}>{step > n ? "✓" : n}</div>
                </div>
                {i < 4 && <div className={`step-line ${step > n ? "done" : ""}`} />}
              </React.Fragment>
            ))}
          </div>
        )}
        {step > 1 && step < 6 && (
          /* Iter 74 — Save & Finish Later. The draft is already being
             synced in the background, so this button just flushes any
             pending debounce and takes the customer to their dashboard
             where the "Continue Booking" strip is waiting. */
          <div style={{ display: "flex", justifyContent: "flex-end", margin: "-8px 0 var(--space-2)" }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              data-testid="save-and-finish-later"
              onClick={async () => {
                try {
                  await api.post("/customer/booking-drafts", { artist_id: id, form, step });
                } catch { /* ignore — sessionStorage still has it */ }
                nav("/customer?tab=drafts");
              }}
              title="Your progress is saved. Pick it up later from any device."
            > Save & Finish Later</button>
          </div>
        )}

        <div className="booking-flow-grid" data-testid="booking-flow-grid">
          <div>
            {step === 1 && (
              <>
                <div className="card card-pad" data-testid="step-1">
                <h2 className="font-serif fs-20 fw-700 mb-8">Choose your Package</h2>
                <p className="text-muted fs-13 mb-20">Select the package that fits your event.</p>
                {packages.map((p) => (
                  <div
                    key={p.id} onClick={() => {
                      setForm((current) => ({
                        ...current,
                        package_id: p.id,
                        outstation_ack: false,
                        travel_terms_acknowledged: false,
                        travel_ack: false,
                      }));
                    }}
                    className={`pkg-card mb-12 ${form.package_id === p.id ? "selected" : ""}`}
                    data-testid={`pkg-opt-${p.id}`}
                  >
                    {p.is_popular && <span className="popular-tag">Most Popular</span>}
                    <div className="flex justify-between items-center" style={{ marginTop: p.is_popular ? "var(--space-3)" : 0 }}>
                      <div>
                        <div className="pkg-name" style={{ fontSize: 18 }}>{p.name}</div>
                        <div className="text-muted fs-12 mt-4"> {p.duration} · {p.features.slice(0, 3).join(" · ")}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-gold font-serif" style={{ fontSize: 24, fontWeight: 700 }}>{fmtINRFull(p.price)}</div>
                        <div className="text-muted fs-11">per event</div>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Iter 76.7 — Add-ons now come EXCLUSIVELY from what the
                    artist configured. The old "Optional Add-ons" grid
                    (Dhol / Anchor / Photography / Extra Hour) was a
                    hard-coded placeholder that appeared for every
                    artist even when they had no real add-ons — and
                    its ids never mapped to real DB rows so the
                    backend snapshot ignored them. If the artist
                    hasn't set any add-ons, the section is simply
                    hidden. */}
                {artistAddons.length > 0 && (
                  <>
                    <h3 className="fs-13 fw-600 mt-24 mb-12 text-gold" style={{ textTransform: "uppercase", letterSpacing: 1 }}> Optional Add-ons</h3>
                    <div className="flex-col gap-10">
                      {artistAddons.map((a) => {
                        const selected = isAddonSelected(a.id);
                        return (
                          <div
                            key={a.id}
                            onClick={() => toggleArtistAddon(a)}
                            className={`pkg-card ${selected ? "selected" : ""} ${a.is_mandatory ? "popular" : ""}`}
                            style={{ padding: "var(--space-3)", cursor: a.is_mandatory ? "default" : "pointer" }}
                            data-testid={`artist-addon-${a.id}`}
                          >
                            {a.is_mandatory && <span className="popular-tag">Mandatory</span>}
                            <div className="flex justify-between items-center" style={{ marginTop: a.is_mandatory ? "var(--space-3)" : 0 }}>
                              <div style={{ flex: 1 }}>
                                <div className="fw-600 fs-14">{a.name}</div>
                                {a.description && <div className="text-muted fs-12">{a.description}</div>}
                              </div>
                              <div className="text-right">
                                <div className="text-gold fw-700 fs-15">+{fmtINRFull(a.price)}</div>
                                {selected && a.max_quantity > 1 && (
                                  <div className="flex items-center gap-8 mt-8" onClick={(e) => e.stopPropagation()}>
                                    <button className="btn btn-ghost btn-xs" onClick={() => setArtistAddonQty(a, addonQty(a.id) - 1)} data-testid={`artist-addon-qty-dec-${a.id}`}>−</button>
                                    <span className="fs-13 fw-600" data-testid={`artist-addon-qty-${a.id}`}>{addonQty(a.id)}</span>
                                    <button className="btn btn-ghost btn-xs" onClick={() => setArtistAddonQty(a, addonQty(a.id) + 1)} data-testid={`artist-addon-qty-inc-${a.id}`}>+</button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                <div className="flex justify-between mt-24">
                  <div />
                  <button className="btn btn-gold" disabled={!pkg} onClick={() => setStep(2)} data-testid="step1-next">Continue to Schedule →</button>
                </div>
              </div>
              </>
            )}

            {step === 2 && (
              <>
              <div className="card card-pad datetime-step" data-testid="step-2">
                <h2 className="datetime-heading">
                  Pick your <span className="datetime-heading-serif">Date &amp; Time</span>
                </h2>
                <p className="text-muted fs-13 mb-20" style={{ paddingBottom: "var(--space-3)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  Choose an available date and performance time. Enter your event city before continuing.
                </p>
                <div className="field mb-20">
                  <div className="field-label">Event City *</div>
                  <CitySearchField
                    className="field-input"
                    id="booking-event-city"
                    city={form.city}
                    cities={cityOptions}
                    popularCities={popularCityOptions}
                    cityAliases={cityAliases}
                    allowCustom
                    onCityChange={(city) => {
                      setAvailabilityLoadFailed(false);
                      if (city.trim().length >= 2) setCityRequiredNotice(false);
                      set("city", city);
                    }}
                    listId="booking-city-options"
                    placeholder="Search or enter any Indian city"
                    data-testid="booking-city"
                  />
                  {cityRequiredNotice && (
                    <div className="text-red fs-12 mt-8" role="alert" data-testid="booking-city-required-notice">
                      Enter your event city before continuing. Your selected date and time are saved.
                    </div>
                  )}
                  {cityOptionsError && (
                    <div className="text-muted fs-11 mt-4">City suggestions are temporarily unavailable. You can still enter your city.</div>
                  )}
                </div>
                <AvailabilityCalendar
                  artistUserId={id}
                  selected={form.event_date}
                  eventCity={form.city}
                  onSchedule={(schedule) => {
                    setAvailabilitySchedule(schedule);
                    setAvailabilityScheduleCity(form.city);
                    setAvailabilityLoadFailed(!schedule);
                  }}
                  onPick={(d) => setForm((current) => ({ ...current, event_date: d, event_time: "" }))}
                />
                {form.event_date && (
                  <div className="datetime-selected-pill" data-testid="selected-date-pill">
                    <span aria-hidden style={{ fontSize: 15 }}></span>
                    <span>{new Date(form.event_date + "T00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
                  </div>
                )}
                {form.event_date && isTodayEventDate(form.event_date, currentTime) && (
                  <p className="text-red fs-12 mt-12" role="alert" data-testid="same-day-booking-unavailable">
                    Same-day bookings are not available. Please choose a future date.
                  </p>
                )}
                {form.event_date && (
                  <div className="field mt-20 timeslot-block">
                    <div className="fs-14 fw-700 mb-12">Select Performance Start Time</div>
                    <div className="timeslot-grid">
                      {TIME_SLOTS.map((t) => {
                        const active = form.event_time === t.time;
                        const available = isTimeSlotAvailable(t.time);
                        return (
                          <button
                            key={t.time}
                            type="button"
                            onClick={() => available && set("event_time", t.time)}
                            disabled={!available}
                            className={`timeslot-card ${active ? "active" : ""}${available ? "" : " unavailable"}`}
                            data-testid={`time-${t.time.replace(/[^0-9]/g, "")}`}
                            title={!form.city ? "Choose the event city first" : available ? "" : "Unavailable for artist preparation time, package duration, or an existing booking"}
                          >
                            <div className="timeslot-time">{t.time}</div>
                            <div className="timeslot-label">{t.label}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {availabilityLoadFailed && form.city && (
                  <p className="text-muted fs-12 mt-12" role="alert">Could not verify availability. Please retry before continuing.</p>
                )}
                {form.city && form.event_date && !availabilityLoadFailed && availabilityScheduleCity !== form.city && (
                  <p className="text-muted fs-12 mt-12" role="status">Checking available times for this city…</p>
                )}
                {!availabilityLoadFailed && form.city && form.event_date && availabilityScheduleCity === form.city && availabilitySchedule && !TIME_SLOTS.some((slot) => isTimeSlotAvailable(slot.time)) && (
                  <p className="text-muted fs-12 mt-12" role="status">No time slots are available for this city and the artist’s schedule.</p>
                )}
                <div className="flex justify-between mt-24">
                  <button className="btn btn-ghost" onClick={() => setStep(1)} data-testid="step2-back">← Back</button>
                  <button className="btn btn-gold" disabled={scheduleContinueDisabled} onClick={continueToEventInfo} data-testid="step2-next">
                    Continue to Event Info →
                  </button>
                </div>
              </div>
              {form.event_date && (
                <SuggestedArtistsPanel
                  artistId={id}
                  date={form.event_date}
                  eventCity={form.city}
                  eventTime={form.event_time}
                  durationMinutes={bookingDurationMinutes}
                  cartArtistIds={cartArtistIds}
                  onOpenAdd={(a) => setAddModalArtist(a)}
                />
              )}
              {/* Multi-artist cart preview once anyone added.
                  Mirrors the left-side Continue button so users don't have
                  to scroll back up after adding more artists at the bottom
                  (user request, Iter 52.5). */}
              {isMultiEvent && (
                <BookingCart
                  items={cartItems}
                  pricing={cartPricing}
                  paymentDue={paymentDue}
                  paymentDuePercent={paymentDuePercent}
                  remainingAfterAdvance={remainingAfterAdvance}
                  onRemove={removeSecondaryArtist}
                  onContinue={continueToEventInfo}
                  continueDisabled={scheduleContinueDisabled}
                  continueLabel="Continue to Event Details →"
                  continueTestId="cart-step2-next"
                />
              )}
              </>
            )}

            {step === 3 && (
              <div className="card card-pad" data-testid="step-3">
                <h2 className="font-serif fs-20 fw-700 mb-8">Tell us about your Event</h2>
                <p className="text-muted fs-13 mb-20">Help the artist prepare the perfect performance.</p>
                <div className="field-row">
                  <div className="field">
                    <div className="field-label">Your Full Name *</div>
                    <input className="field-input" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="booking-name" />
                  </div>
                  <div className="field">
                    <div className="field-label">Mobile Number *</div>
                    <input className="field-input" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="booking-phone" />
                  </div>
                </div>
                <div className="field-row">
                  <div className="field">
                    <div className="field-label">Email *</div>
                    <input className="field-input" type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} data-testid="booking-email" />
                  </div>
                  <div className="field">
                    <div className="field-label">Event Type *</div>
                    <select
                      className="field-input"
                      value={form.event_type}
                      onChange={(e) => setForm((current) => ({
                        ...current,
                        event_type: e.target.value,
                        event_type_selected: true,
                        event_type_other: "",
                      }))}
                      data-testid="booking-event-type"
                    >
                      <option value="">Select an event type</option>
                      <option>Wedding / Sangeet</option><option>Corporate Event</option><option>Birthday Celebration</option>
                      <option>Private Concert</option><option>College Fest</option>
                      <option>Product Launch</option><option>Award Function</option>
                      <option>Festival</option><option>Conference</option>
                      <option>Others</option>
                    </select>
                  </div>
                </div>
                {form.event_type === "Others" && (
                  <div className="field">
                    <div className="field-label">Please specify your event type *</div>
                    <input
                      className="field-input"
                      value={form.event_type_other || ""}
                      onChange={(e) => set("event_type_other", e.target.value)}
                      placeholder="e.g. Anniversary Gala, Store Opening…"
                      maxLength={200}
                      data-testid="booking-event-type-other"
                    />
                  </div>
                )}
                <div className="field-row">
                  <div className="field">
                    <div className="field-label">No. of Days *</div>
                    <input
                      className="field-input" type="number" min="1" max="30"
                      value={form.number_of_days || 1}
                      onChange={(e) => set("number_of_days", parseInt(e.target.value) || 1)}
                      data-testid="booking-number-of-days"
                    />
                  </div>
                  <div className="field">
                    <div className="field-label">Venue *</div>
                    <VenueSearchField
                      className="field-input"
                      venue={form.venue}
                      city={form.city}
                      onVenueChange={(venue) => set("venue", venue)}
                      onAddressChange={(venueAddress) => set("venue_address", venueAddress)}
                      placeholder={`Search venues in ${form.city || "your event city"}`}
                      data-testid="booking-venue"
                    />
                  </div>
                </div>
                <div className="field-row">
                  <div className="field">
                    <div className="field-label">Full Address *</div>
                    <input
                      className="field-input"
                      value={form.venue_address || ""}
                      onChange={(e) => set("venue_address", e.target.value)}
                      placeholder="Select a venue above or enter the full event address"
                      maxLength={500}
                      data-testid="booking-venue-address"
                    />
                  </div>
                </div>

                {/* Outstation Business Rule — auto-shown when event city ≠ artist city (alias-aware) */}
                {isOutstation && (
                  <div
                    className="card card-pad mb-16"
                    style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.35)" }}
                    data-testid="outstation-notice"
                  >
                    <div className="flex gap-8" style={{ alignItems: "flex-start" }}>
                      <div style={{ fontSize: 24, lineHeight: 1 }}></div>
                      <div style={{ flex: 1 }}>
                        <div className="fw-700 text-gold fs-13 mb-4" style={{ textTransform: "uppercase", letterSpacing: 1 }}>
                          Outstation Booking Notice
                        </div>
                        <div className="fs-13" style={{ lineHeight: 1.5, whiteSpace: "pre-line" }}>
                          {buildOutstationNotice(pkg, null)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {musicRequestPrompt && (
                  <div className="field" data-testid="artist-specific-booking-question">
                    <div className="field-label">{musicRequestPrompt.label}</div>
                    <textarea
                      className="field-input"
                      value={form.notes}
                      onChange={(e) => set("notes", e.target.value)}
                      placeholder={musicRequestPrompt.placeholder}
                      data-testid="booking-notes"
                    />
                  </div>
                )}
                <div className="field">
                  <div className="field-label">
                    Special Instructions
                    {isOutstation && <span className="text-gold fs-11" style={{ marginLeft: 8 }}>· recommended for outstation bookings</span>}
                  </div>
                  <textarea
                    className="field-input"
                    value={form.special_instructions || ""}
                    onChange={(e) => set("special_instructions", e.target.value)}
                    placeholder={isOutstation
                      ? "Outstation asks: hotel preference, flight class, arrival/departure timing, dietary needs, green-room setup, security…"
                      : "Anything else the artist should know — dietary needs, green-room setup, dress code, load-in access…"}
                    rows={3}
                    data-testid="booking-special-instructions"
                  />
                </div>
                <div className="field">
                  <div className="field-label">Coupon Code (optional)</div>
                  <input className="field-input" value={form.coupon_code} onChange={(e) => set("coupon_code", e.target.value.toUpperCase())} placeholder="e.g. WEDDING20" data-testid="booking-coupon" />
                </div>
                <div className="flex justify-between mt-24">
                  <button className="btn btn-ghost" onClick={() => setStep(2)} data-testid="step3-back">← Back</button>
                  <button
                    className="btn btn-gold"
                    disabled={
                      !form.customer_name
                      || !form.event_type
                      || (form.event_type === "Others" && !form.event_type_other?.trim())
                      || !form.venue
                      || form.city.trim().length < 2
                    }
                    onClick={continueToReview}
                    data-testid="step3-next"
                  >Continue →</button>
                </div>
              </div>
            )}

            {step === 4 && (
              <ReviewStep
                pkg={pkg}
                form={form}
                set={set}
                isOutstation={isOutstation}
                platformSettings={platformSettings}
                eventItems={eventTravelItems}
                onBack={() => setStep(3)}
                onNext={() => setStep(5)}
                artistId={id}
                techRiderFile={artist.tech_rider_file}
              />
            )}

            {step === 5 && (
              <PaymentStep
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                gatewayInfo={gatewayInfo}
                busy={busy}
                paymentDue={paymentDue}
                paymentTimeline={paymentTimeline}
                eventDate={form.event_date}
                timelineDetails={{ artistName: artist?.profile?.stage_name, packageName: pkg?.name, city: form.city, total: bookingTotal }}
                isMultiEvent={isMultiEvent}
                cartItems={cartItems}
                onBack={() => setStep(4)}
                onSubmit={submitBooking}
              />
            )}

            {step === 6 && successData && (
              <div className="card card-pad text-center" data-testid="step-success">
                <div style={{ fontSize: 72, marginBottom: "var(--space-3)" }}></div>
                <h2 className="font-serif" style={{ fontSize: 40, fontWeight: 700, marginBottom: "var(--space-2)" }}>Booking Confirmed!</h2>
                <p className="text-muted mb-20">
                  {successData.batch ? (
                    <>Your event with <strong>{successData.count} artists</strong> is officially booked. All artists have been notified.</>
                  ) : (
                    <>Your booking with <strong>{artist.profile.stage_name}</strong> is officially confirmed. The artist has been notified.</>
                  )}
                </p>
                <div className="pill pill-gold mb-24" style={{ fontSize: 14, padding: "var(--space-2) 16px" }} data-testid="booking-ref">
                  {successData.batch ? `Event Refs: ${(successData.refs || []).join(" · ")}` : `Booking Ref: ${successData.ref}`}
                </div>
                {successData.batch ? (
                  <div className="card card-pad mb-20" style={{ textAlign: "left", maxWidth: 520, margin: "0 auto var(--space-4)" }}>
                    <div className="fs-13 mb-12">
                      <div className="text-muted">Event Date</div>
                      <div className="fw-700">{form.event_date} · {form.event_time}</div>
                    </div>
                    <div className="fs-13 mb-12">
                      <div className="text-muted">Venue</div>
                      <div className="fw-700">{form.venue}, {form.city}</div>
                    </div>
                    <div className="fs-13">
                      <div className="text-muted mb-8">Artists in this Event</div>
                      {cartItems.map((c) => (
                        <div key={c.artist_id} className="flex justify-between fs-12" style={{ padding: "var(--space-1) 0", borderBottom: "1px dashed rgba(255,255,255,0.06)" }}>
                          <span>{c.artist_name}<span className="text-muted"> · {c.category}</span></span>
                          <span className="text-gold">{fmtINRFull(c.price_subtotal || c.package_price || 0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="card card-pad mb-20" style={{ textAlign: "left", maxWidth: 480, margin: "0 auto var(--space-4)" }}>
                    <div className="flex justify-between mb-8"><span className="text-muted">Artist</span><span>{artist.profile.stage_name}</span></div>
                    <div className="flex justify-between mb-8"><span className="text-muted">Package</span><span>{pkg?.name}</span></div>
                    <div className="flex justify-between mb-8"><span className="text-muted">Date</span><span>{form.event_date} · {form.event_time}</span></div>
                    <div className="flex justify-between mb-8"><span className="text-muted">Venue</span><span>{form.venue}</span></div>
                    <div className="flex justify-between mb-8"><span className="text-muted">Artist Performance Fee</span><span>{fmtINRFull(artistFee)}</span></div>
                    <div className="flex justify-between"><span className="text-muted">Paid to BookTalent</span><span className="text-green">{fmtINRFull(total)}</span></div>
                    <div className="text-muted fs-11 mt-12" style={{ marginTop: "var(--space-3)", lineHeight: 1.4 }}>
                      {quoteMeta?.is_service_artist
                        ? " BookTalent collects the artist fee. The agreed commission is deducted from the artist payout."
                        : <> The Artist Performance Fee of <b>{fmtINRFull(artistFee)}</b> will be settled directly with the artist. BookTalent collects only the Platform Fee.</>}
                    </div>
                  </div>
                )}
                <div className="flex gap-12 justify-center" style={{ flexWrap: "wrap" }}>
                  <button className="btn btn-gold" onClick={() => nav("/customer")} data-testid="success-go-dashboard"> Go to Dashboard</button>
                  {successData.event_id && (
                    <button
                      className="btn btn-ghost"
                      onClick={() => window.open(`/recap/${successData.event_id}`, "_blank", "noopener")}
                      data-testid="success-share-recap"
                    > Share Event Recap</button>
                  )}
                  <button
                    className="btn btn-ghost"
                    style={{ display: successData.batch ? "none" : undefined }}
                    onClick={async () => {
                      const r = await fetch(`${api.defaults.baseURL}/bookings/${successData.booking.id}/invoice`, { credentials: "include" });
                      const blob = await r.blob();
                      const a = document.createElement("a");
                      a.href = window.URL.createObjectURL(blob);
                      a.download = `invoice_${successData.ref}.pdf`;
                      a.click();
                    }}
                    data-testid="success-dl-invoice"
                  > Download Invoice</button>
                  <button className="btn btn-ghost" onClick={() => nav("/")} data-testid="success-go-home"> Home</button>
                </div>

                {/* Iter 44 — Complete-your-event suggestion strip */}
                {suggested.length > 0 && successData.event_id && (
                  <div className="event-strip" data-testid="event-strip">
                    <div className="event-strip-head">
                      <div>
                        <div className="event-strip-title">Complete your event </div>
                        <div className="event-strip-sub">Same date, same city — add another artist and it joins this event automatically.</div>
                      </div>
                    </div>
                    <div className="event-strip-scroll">
                      {suggested.map((s) => {
                        const qs = new URLSearchParams({
                          event_id: successData.event_id,
                          date: form.event_date,
                          time: form.event_time,
                          city: form.city,
                          venue: form.venue,
                          event_type: form.event_type,
                        }).toString();
                        return (
                          <a
                            key={s.user_id}
                            href={`/book/${s.user_id}?${qs}`}
                            className="event-strip-card"
                            data-testid={`event-suggest-${s.user_id}`}
                          >
                            <div className="event-strip-thumb">
                              <span>{s.emoji || ""}</span>
                            </div>
                            <div className="event-strip-name">{s.stage_name || s.name}</div>
                            <div className="event-strip-cat">{s.category}{s.city ? ` · ${s.city}` : ""}</div>
                            <div className="event-strip-add">+ Add to event</div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {step < 6 && (
            <div data-testid="order-summary">
              {isMultiEvent ? (
                <div className="booking-flow-summary">
                  <BookingCart
                    items={cartItems}
                    pricing={cartPricing}
                    paymentDue={paymentDue}
                    paymentDuePercent={paymentDuePercent}
                    remainingAfterAdvance={remainingAfterAdvance}
                    onRemove={removeSecondaryArtist}
                    compact
                  />
                </div>
              ) : (
              <div className="card card-pad booking-flow-summary">
                <button
                  type="button"
                  className="booking-summary-artist-preview"
                  onClick={openArtistPreview}
                  aria-label={`Preview ${artist.profile.stage_name}'s details`}
                  data-testid="booking-artist-preview"
                >
                  <div
                    className="booking-summary-artist-avatar"
                    style={artist.profile.profile_image ? { backgroundImage: `url("${mediaUrl(artist.profile.profile_image)}")` } : undefined}
                    aria-hidden="true"
                  >
                    {!artist.profile.profile_image && (artist.profile.emoji || artist.profile.stage_name?.charAt(0) || "")}
                  </div>
                  <div className="booking-summary-artist-info">
                    <span className="booking-summary-preview-label">Artist preview</span>
                    <div className="fw-700 font-serif fs-16">{artist.profile.stage_name}</div>
                    <div className="text-muted fs-11">{artist.profile.category} · {artist.profile.city}</div>
                  </div>
                  <span className="booking-summary-preview-action" aria-hidden="true">View details</span>
                </button>
                <div className="divider" style={{ margin: "var(--space-3) 0" }} />
                {artist.tech_rider_file?.mime === "application/pdf" && (
                  <div className="booking-tech-rider-summary" data-testid="booking-tech-rider-summary">
                    <div className="fw-700 fs-12">Production information</div>
                    <div className="text-muted fs-11 mt-4 mb-8">
                      Technical requirements for this artist.
                    </div>
                    <TechRiderLink
                      artistId={id}
                      filename={artist.tech_rider_file.filename || "Tech Rider PDF"}
                      className="btn btn-ghost btn-block"
                      testId="booking-tech-rider-link"
                    />
                  </div>
                )}
                <div className="flex justify-between mb-8 fs-13"><span className="text-muted">Artist Performance Fee</span><span>{fmtINRFull(artistFee)}</span></div>
                {addonsTotal > 0 && (
                  <div className="flex justify-between mb-8 fs-11" style={{ marginLeft: 12 }}><span className="text-muted">  Package {fmtINRFull(pkgPrice)} + basic add-ons {fmtINRFull(addonsTotal)}</span></div>
                )}
                {artistAddonsTotal > 0 && (
                  <div className="flex justify-between mb-8 fs-11" style={{ marginLeft: 12 }} data-testid="summary-artist-addons"><span className="text-muted">  + Artist add-ons {fmtINRFull(Math.round(artistAddonsTotal))}</span></div>
                )}
                <div className="divider" style={{ margin: "var(--space-2) 0" }} />
                {quoteMeta?.is_service_artist ? (
                  <>
                    {/* Service artists: show the notional 5% fee, waiver line and payable=0
                        so the customer clearly understands they're getting a waiver worth ₹X. */}
                    {(() => {
                      const notionalPct = feePercent || 5;
                      const notionalFee = Math.round((artistFee * notionalPct) / 100);
                      return (
                        <>
                          <div className="flex justify-between mb-8 fs-13" data-testid="summary-fee-notional">
                            <span className="text-muted">Platform Service Fee ({notionalPct}%)</span>
                            <span style={{ textDecoration: "line-through", opacity: 0.6 }}>{fmtINRFull(notionalFee)}</span>
                          </div>
                          <div className="flex justify-between mb-8 fs-13" data-testid="summary-fee-waiver">
                            <span className="text-good">Platform Fee Waiver</span>
                            <span className="text-good">−{fmtINRFull(notionalFee)}</span>
                          </div>
                          <div className="flex justify-between mb-8 fs-13" data-testid="summary-fee-payable">
                            <span className="text-muted">Platform Fee Payable</span>
                            <span>{fmtINRFull(0)}</span>
                          </div>
                          <div
                            className="text-good fs-12 mb-8"
                            data-testid="summary-fee-waiver-banner"
                            style={{ padding: "var(--space-2) 10px", borderRadius: 8, background: "rgba(46,204,113,0.08)", border: "1px solid rgba(46,204,113,0.25)", marginTop: "var(--space-1)" }}
                          >
                             Your 5% Platform Fee has been waived for this artist.
                          </div>
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <div className="flex justify-between mb-8 fs-13"><span className="text-muted">Platform Service Fee ({feePercent}%)</span><span>{fmtINRFull(platformFee)}</span></div>
                )}
                {gstVisible && gst > 0 && (
                  <div className="flex justify-between mb-8 fs-13"><span className="text-muted">GST ({gstPercent}% on {quoteMeta?.is_service_artist ? "Artist Fee" : "Platform Fee"})</span><span>{fmtINRFull(gst)}</span></div>
                )}
                <div className="divider" style={{ margin: "var(--space-3) 0" }} />
                <div className="flex justify-between mb-12" data-testid="summary-total-row">
                  <span className="fw-700 font-serif fs-16">{quoteMeta?.is_service_artist ? "Total Payable Amount" : "Total Booking Value"}</span>
                  <span className="fw-700 text-gold font-serif fs-20">{fmtINRFull(bookingTotal)}</span>
                </div>
                <div style={{ background: "var(--gold-dim)", padding: "var(--space-3)", borderRadius: 10 }}>
                  <div className="text-muted fs-11 mb-4"> Booking Advance ({paymentDuePercent}%)</div>
                  <div className="font-serif fs-20 fw-700 text-gold" data-testid="token-amount">{fmtINRFull(paymentDue)}</div>
                  <div className="text-muted fs-11 mt-8" style={{ marginTop: "var(--space-2)", lineHeight: 1.4 }}>
                     Remaining scheduled amount: <b>{fmtINRFull(remainingAfterAdvance)}</b>.
                  </div>
                </div>

                {paymentTimeline.length > 0 && (
                  <PaymentTimeline
                    rows={paymentTimeline}
                    eventDate={form.event_date}
                    artistName={artist?.profile?.stage_name}
                    packageName={pkg?.name}
                    city={form.city}
                    total={bookingTotal}
                    compact
                    testId="booking-summary-payment-timeline"
                  />
                )}

                {/* Fee-Inclusion Note — always visible under the totals */}
                <div
                  className="text-muted fs-11 mt-12"
                  style={{ padding: "var(--space-2)", borderRadius: 8, background: "rgba(255,255,255,0.03)", lineHeight: 1.5 }}
                  data-testid="booking-fee-note"
                >
                  Travel, accommodation, and hospitality terms depend on the selected package and event location. Review the travel terms before confirming.
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      </div>

      {artistPreviewOpen && (
        <div
          className="booking-artist-preview-overlay"
          onClick={() => setArtistPreviewOpen(false)}
          data-testid="booking-artist-preview-dialog"
        >
          <section
            className="booking-artist-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="booking-artist-preview-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="booking-artist-preview-close"
              onClick={() => setArtistPreviewOpen(false)}
              aria-label="Close artist preview"
              data-testid="booking-artist-preview-close"
            >×</button>
            <div className="booking-artist-preview-heading">
              <div
                className="booking-summary-artist-avatar"
                style={artist.profile.profile_image ? { backgroundImage: `url("${mediaUrl(artist.profile.profile_image)}")` } : undefined}
                aria-hidden="true"
              >
                {!artist.profile.profile_image && (artist.profile.emoji || artist.profile.stage_name?.charAt(0) || "")}
              </div>
              <div>
                <span className="booking-summary-preview-label">Artist at a glance</span>
                <h2 id="booking-artist-preview-title">{artist.profile.stage_name}</h2>
                <p>{artist.profile.category}{artist.profile.city ? ` · ${artist.profile.city}` : ""}</p>
              </div>
            </div>
            <div className="booking-artist-preview-content">
              <div className="booking-artist-preview-facts">
                {Number(artist.profile.rating_avg) > 0 && (
                  <div><strong>★ {Number(artist.profile.rating_avg).toFixed(1)}</strong><span>{Number(artist.profile.review_count) || 0} reviews</span></div>
                )}
                {Number(artist.profile.experience_years) > 0 && (
                  <div><strong>{Number(artist.profile.experience_years)} years</strong><span>On stage</span></div>
                )}
                {artist.profile.languages?.length > 0 && (
                  <div><strong>Languages</strong><span>{artist.profile.languages.join(" · ")}</span></div>
                )}
              </div>
              {artistBioPreview && (
                <section>
                  <h3>About</h3>
                  <p className="booking-artist-preview-bio">{artistBioPreview}</p>
                </section>
              )}
              {artistEventTypeList.length > 0 && (
                <section>
                  <h3>Performs at</h3>
                  <p className="booking-artist-preview-copy">{artistEventTypeList.slice(0, 5).join(" · ")}</p>
                </section>
              )}
              {packages.length > 0 && (
                <section>
                  <h3>Packages</h3>
                  <div className="booking-artist-preview-packages">
                    {packages.slice(0, 3).map((item) => (
                      <div key={item.id}>
                        <strong>{item.name}</strong>
                        <span>{item.duration} · {fmtINRFull(item.price)}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              <a
                className="btn btn-ghost booking-artist-preview-full-profile"
                href={`/artist/${artist.profile.slug || id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-artist-preview-full-profile"
              >
                View full profile <span aria-hidden="true">↗</span>
              </a>
            </div>
          </section>
        </div>
      )}

      {addModalArtist && (
        <AddArtistToCartModal
          suggestedArtist={addModalArtist}
          eventCity={form.city}
          onAdd={addSecondaryArtist}
          onClose={() => setAddModalArtist(null)}
        />
      )}

      {alternatives && (
        <div className="modal-bg" onClick={() => setAlternatives(null)} data-testid="alternatives-modal">
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title"> Date Not Available</div>
            <div className="modal-sub">{alternatives.message} on {alternatives.date}. Here are some great alternatives:</div>
            {alternatives.list.length === 0 ? (
              <div className="empty"><div className="empty-icon"></div><div className="empty-title">No alternatives found</div><p className="fs-13">Try picking a different date.</p></div>
            ) : (
              <div className="flex-col gap-12">
                {alternatives.list.map((a) => (
                  <div
                    key={a.user_id} className="card card-pad flex items-center gap-12"
                    onClick={() => { window.location.href = `/artist/${a.user_id}`; }}
                    style={{ cursor: "pointer" }}
                    data-testid={`alt-${a.user_id}`}
                  >
                    <div className="avatar avatar-lg" style={{ background: "linear-gradient(135deg, var(--purple), var(--gold))", width: 48, height: 48, fontSize: 22 }}>{a.emoji || ""}</div>
                    <div style={{ flex: 1 }}>
                      <div className="fw-700 font-serif">{a.stage_name}</div>
                      <div className="text-muted fs-12">{a.category} ·  {a.city}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button className="btn btn-ghost btn-block mt-16" onClick={() => { setAlternatives(null); setStep(2); }} data-testid="alt-pick-different-date">Pick a Different Date</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Suggested Artists panel — cross-sell during date-pick step ─────────
function SuggestedArtistsPanel({ artistId, date, eventCity, eventTime, durationMinutes, cartArtistIds, onOpenAdd }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (!artistId || !date) return;
    setLoading(true);
    const query = new URLSearchParams({ date_str: date, limit: "6" });
    if (eventCity) query.set("event_city", eventCity);
    if (eventTime) query.set("event_time", eventTime);
    if (durationMinutes) query.set("duration_minutes", String(durationMinutes));
    if (cartArtistIds?.size) query.set("exclude_ids", Array.from(cartArtistIds).join(","));
    api.get(`/artists/${artistId}/suggested?${query.toString()}`)
      .then((r) => setItems(r.data?.suggested || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [artistId, date, eventCity, eventTime, durationMinutes, cartArtistIds]);

  if (loading) {
    return <div className="card card-pad mt-16 text-center text-muted fs-13" data-testid="suggested-loading">Finding complementary artists…</div>;
  }
  if (items.length === 0) return null;
  const hasAdd = typeof onOpenAdd === "function";
  return (
    <div className="card card-pad mt-16 suggested-panel" data-testid="suggested-artists">
      <div className="smart-panel-head">
        <div>
          <div className="smart-panel-title">Need More Artists for This Event?</div>
          <div className="smart-panel-sub">Available for your selected time on {date} — add them to the same booking, one checkout, separate contracts.</div>
        </div>
      </div>
      <div className="suggested-grid">
        {items.map((a) => {
          const alreadyInCart = cartArtistIds?.has?.(a.user_id);
          return (
            <div key={a.user_id} className="suggested-card" data-testid={`suggested-${a.user_id}`}>
              <div
                className="suggested-thumb"
                style={a.profile_image ? {
                  background: `linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.7)), url(${/^https?:\/\//.test(a.profile_image) ? a.profile_image : `${api.defaults.baseURL}/media/${a.profile_image}`}) center/cover`,
                } : {}}
              >
                {!a.profile_image && <span style={{ fontSize: 40 }}></span>}
              </div>
              <div className="suggested-body">
                <div className="fw-700 fs-13">{a.stage_name}</div>
                <div className="text-muted fs-11">{a.category}</div>
                <div className="suggested-foot">
                  {a.starting_price && <span className="text-gold fw-600 fs-12">{fmtINRFull(a.starting_price)}</span>}
                </div>
                {hasAdd ? (
                  <button
                    type="button"
                    className={`suggested-add-btn ${alreadyInCart ? "added" : ""}`}
                    disabled={alreadyInCart}
                    onClick={() => onOpenAdd(a)}
                    data-testid={`add-to-event-${a.user_id}`}
                    title={alreadyInCart ? "This artist is already in your event cart" : "Add this artist to your event"}
                  >
                    {alreadyInCart ? "✓ Already in your event" : "+ Add to Event"}
                  </button>
                ) : (
                  <a
                    href={`/artist/${a.slug || a.user_id}`}
                    className="suggested-add-btn"
                    data-testid={`view-artist-${a.user_id}`}
                  >View Profile →</a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
